import { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, RefreshCw, Clock } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const NEXT = { confirmed: "preparing", preparing: "ready", ready: "completed" };
const LABEL = { confirmed: "Empezar a preparar", preparing: "Marcar listo", ready: "Completar" };
const COLOR = {
  pending: "bg-neutral-100 text-neutral-600", confirmed: "bg-blue-100 text-blue-700",
  preparing: "bg-amber-100 text-amber-700", ready: "bg-emerald-100 text-emerald-700",
  completed: "bg-neutral-100 text-neutral-500", cancelled: "bg-red-100 text-red-600",
};

export default function OrdersBoard() {
  const [orders, setOrders] = useState(null);
  const [scope, setScope] = useState("active");

  const load = async (sc = scope) => {
    try {
      const { data } = await api.get(`/owner/orders?scope=${sc}`);
      setOrders(data);
    } catch (e) { toast.error(apiErr(e)); }
  };

  useEffect(() => {
    load();
    const t = setInterval(() => load(), 5000);
    return () => clearInterval(t);
  }, [scope]);

  const advance = async (o) => {
    const next = NEXT[o.status];
    if (!next) return;
    try {
      await api.put(`/owner/orders/${o.id}/status`, { status: next });
      toast.success("Pedido actualizado");
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  if (!orders) return <div className="py-20 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {["active", "all"].map((s) => (
            <button key={s} onClick={() => setScope(s)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${scope === s ? "bg-neutral-900 text-white" : "bg-white text-neutral-600 border border-neutral-200"}`}
              data-testid={`orders-scope-${s}`}>
              {s === "active" ? "Activos" : "Todos"}
            </button>
          ))}
        </div>
        <Button variant="ghost" size="sm" onClick={() => load()} data-testid="orders-refresh"><RefreshCw className="w-4 h-4 mr-1" /> Actualizar</Button>
      </div>

      {orders.length === 0 ? (
        <div className="rounded-xl border border-dashed border-neutral-300 bg-white py-16 text-center text-neutral-400">
          <Clock className="w-8 h-8 mx-auto mb-2" /> No hay pedidos {scope === "active" ? "activos" : "todavía"}.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {orders.map((o) => (
            <div key={o.id} className="rounded-xl border border-neutral-200 bg-white p-4" data-testid={`order-card-${o.id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-admin font-bold">#{o.id.slice(0, 8)}</span>
                <Badge className={`${COLOR[o.status]} border-0 capitalize`}>{o.status}</Badge>
              </div>
              <div className="text-sm text-neutral-500 mb-2">{o.customer_name || "Cliente"} · {o.customer_phone || "—"}</div>
              {o.customer_address && <div className="text-xs text-neutral-500 mb-2" data-testid={`order-address-${o.id}`}>{o.customer_address}</div>}
              <div className="space-y-1 mb-3">
                {o.items.map((it) => (
                  <div key={it.id} className="flex justify-between text-sm">
                    <span>{it.quantity}× {it.name}</span>
                    <span className="tabular-nums text-neutral-500">{money(it.line_total)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t pt-2 space-y-0.5 text-sm">
                <div className="flex justify-between"><span className="text-neutral-500">Total</span><span className="font-bold tabular-nums">{money(o.total)}</span></div>
                <div className="flex justify-between"><span className="text-neutral-400">Comisión</span><span className="tabular-nums text-neutral-400">-{money(o.commission_amount)}</span></div>
                <div className="flex justify-between"><span className="text-accent-foreground">Tu neto</span><span className="tabular-nums font-medium text-accent-foreground">{money(o.net_amount)}</span></div>
              </div>
              {NEXT[o.status] && (
                <Button size="sm" className="w-full mt-3" onClick={() => advance(o)} data-testid={`advance-order-${o.id}`}>{LABEL[o.status]}</Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
